import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReferralDigestCard from './ReferralDigestCard';
import { AlertTriangle, BadgeCheck, BarChart3, Building2, Check, ChevronDown, ChevronUp, FileText, Mail, PencilLine, RefreshCw, Save, Send, Settings2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AnalyticsCard({ token }) {
  const [data, setData] = useState(null);
  const [pclicks, setPclicks] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${API_URL}/api/admin/locator/analytics?days=${days}`, { headers }),
          fetch(`${API_URL}/api/admin/locator/profile-clicks?days=${days}`, { headers }),
        ]);
        setData(await r1.json());
        setPclicks(await r2.json());
      } catch (e) { console.error('locator analytics fetch failed', e); }
    })();
  }, [token, days]);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5" data-testid="locator-analytics-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-violet-600" /> Locator Analytics</h3>
          <p className="text-xs text-gray-500 mt-1">
            {data ? `${data.total_page_views} page views · ${data.total_claim_clicks} claim-link clicks in last ${days} days` : 'Loading…'}
          </p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${days === d ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`analytics-days-${d}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>
      {data && (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase text-gray-400 mb-1.5">Top Pages by Views</p>
            {data.pages.length === 0 ? <p className="text-xs text-gray-400 py-3">No page views yet.</p> : (
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-50 border border-gray-100 rounded-lg" data-testid="analytics-pages-list">
                {data.pages.map((p) => (
                  <div key={p.page} className="px-3 py-1.5 text-xs flex justify-between gap-2">
                    <span className="text-gray-700 truncate">{p.page}</span>
                    <span className="text-gray-500 font-semibold shrink-0">{p.views}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase text-gray-400 mb-1.5">Claim Interest by Facility</p>
            {data.facilities.length === 0 ? <p className="text-xs text-gray-400 py-3">No claim clicks yet.</p> : (
              <div className="max-h-64 overflow-y-auto divide-y divide-gray-50 border border-gray-100 rounded-lg" data-testid="analytics-facilities-list">
                {data.facilities.map((f) => (
                  <div key={f.slug} className="px-3 py-1.5 text-xs flex justify-between gap-2">
                    <span className="text-gray-700 truncate">
                      {f.name} <span className="text-gray-400">· {f.city}{f.state ? `, ${f.state}` : ''}</span>
                      {f.claimed && <span className="ml-1.5 px-1 py-0.5 text-[9px] font-bold uppercase rounded bg-emerald-50 text-emerald-600">Claimed</span>}
                    </span>
                    <span className="text-gray-500 font-semibold shrink-0">{f.clicks}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {pclicks && (
        <div className="mt-4">
          <p className="text-[11px] font-bold uppercase text-gray-400 mb-1.5">Booking-Link Clicks — Facility Profiles</p>
          {pclicks.facilities.length === 0 ? (
            <p className="text-xs text-gray-400" data-testid="analytics-profile-clicks-empty">No profile CTA clicks yet — they'll show here once visitors click booking links on facility profiles (e.g. Sanctuary, HardKore).</p>
          ) : (
            <div className="border border-gray-100 rounded-lg divide-y divide-gray-50 max-h-64 overflow-y-auto" data-testid="analytics-profile-clicks">
              {pclicks.facilities.map((f) => (
                <div key={f.slug} className="px-3 py-2">
                  <div className="flex justify-between gap-2 text-xs">
                    <span className="font-semibold text-gray-700 truncate">{f.name}</span>
                    <span className="font-bold text-gray-600 shrink-0">{f.total} click{f.total === 1 ? '' : 's'}</span>
                  </div>
                  {f.labels.map((l) => (
                    <div key={`${l.label}-${l.kind}`} className="flex justify-between gap-2 text-[11px] text-gray-500 mt-0.5 pl-3">
                      <span className="truncate">{l.label}</span>
                      <span className="shrink-0">{l.clicks}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const PROFILE_TEMPLATE = {
  zip: '',
  email_public: '',
  hours: ['Monday–Friday: 9:00 AM – 7:00 PM', 'Saturday–Sunday: 9:00 AM – 5:00 PM'],
  session_note: '',
  audience: 'Who this facility serves.',
  services: ['Service one', 'Service two'],
  primary_cta: { label: 'Book a Session', url: 'https://...' },
  pricing_groups: [
    {
      title: 'Sessions',
      note: '',
      rows: [
        { label: 'Drop-In', price: '$50', note: '', cta_label: 'Book a Drop-In', cta_url: 'https://...' },
        { label: 'Pending item — leave price null, never $0', price: null, note: 'Price pending — contact facility' },
      ],
      cta: { label: 'View All Options', url: 'https://...' },
    },
  ],
  extra_ctas: [{ label: 'Secondary Link', url: 'https://...', secondary: true }],
  related: null,
  seo_title: 'Facility Name in City, ST | Services, Pricing and Booking',
  seo_description: 'One-sentence meta description.',
  schema_type: 'LocalBusiness',
  schema_offers: [['Offer Name', 50]],
};

function ProfileEditorCard({ token }) {
  const [list, setList] = useState([]);
  const [slug, setSlug] = useState('');
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadList = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/profiles`, { headers });
      setList((await res.json()).facilities);
    } catch (e) { console.error('profiles list failed', e); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadList(); }, [loadList]);

  const pick = async (s) => {
    setSlug(s); setErr(''); setMsg(''); setText('');
    if (!s) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/profile/${s}`, { headers });
      const d = await res.json();
      if (d.profile) setText(JSON.stringify(d.profile, null, 2));
    } catch (e) { console.error('profile fetch failed', e); }
  };

  const save = async () => {
    setErr(''); setMsg('');
    let parsed;
    try { parsed = JSON.parse(text); } catch (e) { setErr(`Invalid JSON: ${e.message}`); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/profile/${slug}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ profile: parsed }) });
      const body = await res.json();
      if (!res.ok) { setErr(body.detail || 'Save failed'); return; }
      setMsg(`Saved — live at /locator/facility/${slug}`);
      loadList();
    } catch { setErr('Network error — try again'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remove this profile? The facility card stays in the locator; only the rich profile page is removed.')) return;
    await fetch(`${API_URL}/api/admin/locator/profile/${slug}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ profile: null }) });
    setText(''); setMsg('Profile removed.'); loadList();
  };

  const current = list.find((f) => f.slug === slug);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5" data-testid="profile-editor-card">
      <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-600" /> Facility Profile Editor</h3>
      <p className="text-xs text-gray-500 mt-1 mb-3">
        Rich profile pages at /locator/facility/&#123;slug&#125; — hours, services, pricing tables and booking CTAs. {list.filter((f) => f.has_profile).length} live profile{list.filter((f) => f.has_profile).length === 1 ? '' : 's'}. Leave unknown prices as <code className="bg-gray-100 px-1 rounded">null</code> — they render as "Pricing pending", never free.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={slug} onChange={(e) => pick(e.target.value)} className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg max-w-[320px]" data-testid="profile-editor-select">
          <option value="">Select a facility…</option>
          {list.map((f) => (
            <option key={f.slug} value={f.slug}>{f.has_profile ? '● ' : ''}{f.name} — {f.city}, {f.state}</option>
          ))}
        </select>
        {slug && !text && (
          <button onClick={() => setText(JSON.stringify(PROFILE_TEMPLATE, null, 2))} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200" data-testid="profile-editor-template">
            Start from Template
          </button>
        )}
        {slug && current?.has_profile && (
          <a href={`/locator/facility/${slug}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200" data-testid="profile-editor-view-live">
            View Live ↗
          </a>
        )}
      </div>
      {slug && text && (
        <div className="mt-3">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={16} spellCheck={false} className="w-full px-3 py-2 text-[11px] border border-gray-200 rounded-lg font-mono leading-relaxed" data-testid="profile-editor-textarea" />
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button onClick={save} disabled={saving} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid="profile-editor-save">
              <Save className="w-3 h-3" /> {saving ? 'Saving…' : 'Validate & Save'}
            </button>
            {current?.has_profile && (
              <button onClick={remove} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-50 text-red-500 hover:bg-red-100" data-testid="profile-editor-remove">
                Remove Profile
              </button>
            )}
            {err && <span className="text-xs text-red-500" data-testid="profile-editor-error">{err}</span>}
            {msg && <span className="text-xs text-emerald-600" data-testid="profile-editor-msg">{msg}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_LABELS = { pending: 'Ready for Step 1', claimed: 'Claimed', suppressed: 'Excluded', completed: 'Sequence done', replied: 'Replied', opted_out: 'Opted out' };
const STATUS_BADGES = {
  pending: 'bg-blue-50 text-blue-600',
  claimed: 'bg-emerald-50 text-emerald-600',
  suppressed: 'bg-gray-100 text-gray-500',
  completed: 'bg-violet-50 text-violet-600',
  replied: 'bg-emerald-50 text-emerald-600',
  opted_out: 'bg-red-50 text-red-500',
};

function OutreachCard({ token }) {
  const [data, setData] = useState(null);
  const [run, setRun] = useState(null);
  const [showList, setShowList] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [addr, setAddr] = useState('');
  const [sender, setSender] = useState('Daniel');
  const [savingSettings, setSavingSettings] = useState(false);
  const [editPid, setEditPid] = useState('');
  const [prospect, setProspect] = useState(null);
  const [editStep, setEditStep] = useState(1);
  const [subj, setSubj] = useState('');
  const [bodyTxt, setBodyTxt] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [starting, setStarting] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const pollRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };
  const jsonHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/outreach`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setData(d);
      setAddr(d.settings.mailing_address || '');
      setSender(d.settings.sender_name || 'Daniel');
    } catch (e) { console.error('outreach fetch failed', e); setLoadError(true); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/outreach/status`, { headers });
      const { run: r } = await res.json();
      setRun(r);
      if (r && r.done && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    } catch (e) { console.error('outreach status failed', e); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchData, fetchStatus]);

  useEffect(() => {
    if (run && !run.done && !pollRef.current) pollRef.current = setInterval(fetchStatus, 3000);
  }, [run, fetchStatus]);

  const loadProspect = async (pid) => {
    setEditPid(pid);
    setProspect(null);
    if (!pid) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/outreach/prospect/${pid}`, { headers });
      const p = await res.json();
      setProspect(p);
      const s = p.steps.find((x) => x.step === editStep) || p.steps[0];
      setEditStep(s.step);
      setSubj(s.subject);
      setBodyTxt(s.body);
    } catch (e) { console.error('prospect fetch failed', e); }
  };

  const pickStep = (n) => {
    if (!prospect) return;
    const s = prospect.steps.find((x) => x.step === n);
    if (!s) return;
    setEditStep(n);
    setSubj(s.subject);
    setBodyTxt(s.body);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await fetch(`${API_URL}/api/admin/locator/outreach/settings`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ mailing_address: addr, sender_name: sender }) });
      await fetchData();
    } finally { setSavingSettings(false); }
  };

  const saveDraft = async () => {
    if (!editPid) return;
    setSavingDraft(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/outreach/prospect/${editPid}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ step: editStep, subject: subj, body: bodyTxt }) });
      if (!res.ok) window.alert('Save failed — try again');
      else setProspect((p) => ({ ...p, steps: p.steps.map((s) => (s.step === editStep ? { ...s, subject: subj, body: bodyTxt } : s)) }));
    } finally { setSavingDraft(false); }
  };

  const markProspect = async (pid, field, value) => {
    await fetch(`${API_URL}/api/admin/locator/outreach/prospect/${pid}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ [field]: value }) });
    if (prospect && editPid === pid) setProspect((p) => ({ ...p, [field]: value }));
    fetchData();
  };

  const toggleSuppress = async () => {
    if (!prospect) return;
    const next = !prospect.suppressed;
    await fetch(`${API_URL}/api/admin/locator/outreach/prospect/${editPid}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ suppressed: next }) });
    setProspect((p) => ({ ...p, suppressed: next }));
    fetchData();
  };

  const startBlast = async () => {
    if (!window.confirm(`Send Step 1 (personalized) to ${data.eligible_count} prospects? Steps 2-5 will follow automatically on days 3, 6, 9 and 12 unless they claim or you exclude them.`)) return;
    setStarting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/outreach/blast`, { method: 'POST', headers });
      const body = await res.json();
      if (!res.ok) { window.alert(body.detail || 'Blast failed to start'); return; }
      fetchStatus();
      fetchData();
    } catch { window.alert('Network error — try again'); }
    finally { setStarting(false); }
  };

  if (loadError) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5" data-testid="locator-outreach-card">
        <p className="text-xs text-red-500">Outreach data failed to load — refresh the page or check that you're still logged in.</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5" data-testid="locator-outreach-card">
        <p className="text-xs text-gray-400 flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading outreach…</p>
      </div>
    );
  }

  const inFlight = run && !run.done;
  const st = data.stats.statuses || {};
  const addrMissing = !data.settings.mailing_address;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5" data-testid="locator-outreach-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" /> Personalized Outreach Sequence <span className="text-[10px] font-semibold text-gray-400">5 emails · Days 0 / 3 / 6 / 9 / 12</span></h3>
          <p className="text-xs text-gray-500 mt-1" data-testid="outreach-stats-line">
            {data.stats.total_prospects} prospects · {data.eligible_count} ready for Step 1
            {st.claimed ? ` · ${st.claimed} claimed` : ''}{st.replied ? ` · ${st.replied} replied` : ''}{st.opted_out ? ` · ${st.opted_out} opted out` : ''}{st.suppressed ? ` · ${st.suppressed} excluded` : ''}{st.completed ? ` · ${st.completed} completed` : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowSettings(!showSettings)} className={`px-3 py-1.5 text-xs font-semibold rounded-lg inline-flex items-center gap-1 ${addrMissing ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid="outreach-settings-btn">
            <Settings2 className="w-3 h-3" /> Settings{addrMissing ? ' !' : ''}
          </button>
          <button onClick={() => setShowList(!showList)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 inline-flex items-center gap-1" data-testid="outreach-preview-btn">
            {showList ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Recipients
          </button>
          <button onClick={startBlast} disabled={starting || inFlight || data.eligible_count === 0 || addrMissing} title={addrMissing ? 'Set your business mailing address in Settings first' : ''} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid="outreach-blast-btn">
            <Send className="w-3 h-3" /> Send Step 1 ({data.eligible_count})
          </button>
        </div>
      </div>

      <p className="flex items-start gap-1.5 mt-3 px-3 py-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg" data-testid="outreach-compliance-note">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>This is a cold prospect list. Resend's acceptable-use policy prohibits unsolicited cold email and can suspend accounts — the source doc recommends a separate warmed sending domain and verified addresses. Send at your own discretion; every email includes the opt-out line.</span>
      </p>

      {showSettings && (
        <div className="mt-3 p-3 border border-gray-100 rounded-lg grid sm:grid-cols-[1fr_200px_auto] gap-2 items-end" data-testid="outreach-settings-panel">
          <label className="text-xs text-gray-600">
            <span className="block text-[10px] font-bold uppercase text-gray-400 mb-1">Business Mailing Address (replaces the placeholder in every email — required)</span>
            <input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="e.g. NinjAthlete, 123 Main St Suite 4, Salt Lake City, UT 84101" className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" data-testid="outreach-mailing-address-input" />
          </label>
          <label className="text-xs text-gray-600">
            <span className="block text-[10px] font-bold uppercase text-gray-400 mb-1">Signature Name</span>
            <input value={sender} onChange={(e) => setSender(e.target.value)} className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" data-testid="outreach-sender-name-input" />
          </label>
          <button onClick={saveSettings} disabled={savingSettings} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid="outreach-settings-save">
            <Save className="w-3 h-3" /> Save
          </button>
        </div>
      )}

      {run && (
        <div className="mt-3" data-testid="outreach-progress">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{run.done ? 'Last blast complete' : 'Sending…'} {run.sent}/{run.total}{run.failed?.length ? ` · ${run.failed.length} failed` : ''}</span>
            <span>{run.started_at && new Date(run.started_at).toLocaleString()}</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${run.done ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${run.total ? Math.round((run.sent / run.total) * 100) : 0}%` }} />
          </div>
        </div>
      )}

      {/* Draft browser / editor */}
      <div className="mt-4 border-t border-gray-100 pt-3">
        <div className="flex items-center gap-2 flex-wrap">
          <PencilLine className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-[11px] font-bold uppercase text-gray-400">Preview &amp; edit drafts</span>
          <select value={editPid} onChange={(e) => loadProspect(e.target.value)} className="px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg max-w-[320px]" data-testid="outreach-prospect-select">
            <option value="">Select a facility…</option>
            {data.prospects.map((p) => (
              <option key={p.prospect_id} value={p.prospect_id}>{p.facility} — {p.city}, {p.state} ({STATUS_LABELS[p.status] || p.status})</option>
            ))}
          </select>
          {prospect && (
            <>
              <button onClick={() => markProspect(editPid, 'replied', !prospect.replied)} className={`px-2.5 py-1.5 text-[11px] font-semibold rounded-lg ${prospect.replied ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}`} data-testid="outreach-replied-btn">
                {prospect.replied ? '✓ Replied — undo' : 'Mark Replied'}
              </button>
              <button onClick={() => markProspect(editPid, 'opted_out', !prospect.opted_out)} className={`px-2.5 py-1.5 text-[11px] font-semibold rounded-lg ${prospect.opted_out ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-red-50 text-red-500 hover:bg-red-100'}`} data-testid="outreach-optout-btn">
                {prospect.opted_out ? '✓ Opted out — undo' : 'Mark Opted Out'}
              </button>
              <button onClick={toggleSuppress} className={`px-2.5 py-1.5 text-[11px] font-semibold rounded-lg ${prospect.suppressed ? 'bg-gray-700 text-white hover:bg-gray-800' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} data-testid="outreach-suppress-btn">
                {prospect.suppressed ? 'Re-include' : 'Exclude'}
              </button>
            </>
          )}
        </div>

        {prospect && (
          <div className="mt-3 border border-gray-100 rounded-lg overflow-hidden" data-testid="outreach-draft-editor">
            <div className="flex gap-1.5 px-3 py-2 bg-gray-50 border-b border-gray-100 flex-wrap">
              {prospect.steps.map((s) => (
                <button key={s.step} onClick={() => pickStep(s.step)} className={`px-2.5 py-1 text-[11px] font-semibold rounded ${editStep === s.step ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 border border-gray-200'}`} data-testid={`draft-step-${s.step}`}>
                  Step {s.step} · Day {s.day_offset}
                </button>
              ))}
              <span className="ml-auto text-[11px] text-gray-400 self-center">to {prospect.email}</span>
            </div>
            <div className="p-3 space-y-2">
              <input value={subj} onChange={(e) => setSubj(e.target.value)} className="w-full px-2.5 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg" placeholder="Subject" data-testid="draft-subject-input" />
              <textarea value={bodyTxt} onChange={(e) => setBodyTxt(e.target.value)} rows={11} className="w-full px-2.5 py-2 text-xs border border-gray-200 rounded-lg font-mono leading-relaxed" data-testid="draft-body-input" />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[10px] text-gray-400">[BUSINESS MAILING ADDRESS] is swapped for your saved address at send time. Emails send as plain personal notes (no branding) for deliverability.</p>
                <button onClick={saveDraft} disabled={savingDraft} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid="draft-save-btn">
                  <Save className="w-3 h-3" /> {savingDraft ? 'Saving…' : 'Save Draft'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showList && (
        <div className="mt-3 max-h-64 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50" data-testid="outreach-recipient-list">
          {data.prospects.map((f) => (
            <div key={f.prospect_id} className="px-3 py-1.5 text-xs flex items-center justify-between gap-2">
              <span className="text-gray-700 font-medium truncate flex-1">
                {f.facility} <span className="text-gray-400 font-normal">· {f.city}, {f.state} · {f.email}</span>
              </span>
              <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase rounded shrink-0 ${STATUS_BADGES[f.status] || 'bg-gray-100 text-gray-500'}`}>{STATUS_LABELS[f.status] || f.status}</span>
              {!['replied', 'opted_out', 'claimed'].includes(f.status) && (
                <span className="flex gap-1 shrink-0">
                  <button onClick={() => markProspect(f.prospect_id, 'replied', true)} className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100" data-testid={`recipient-replied-${f.prospect_id}`}>Replied</button>
                  <button onClick={() => markProspect(f.prospect_id, 'opted_out', true)} className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-50 text-red-500 hover:bg-red-100" data-testid={`recipient-optout-${f.prospect_id}`}>Opt-out</button>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LocatorTab() {
  const token = sessionStorage.getItem('admin_token');
  const [status, setStatus] = useState('pending');
  const [subs, setSubs] = useState(null);
  const [busy, setBusy] = useState(null);

  const fetchSubs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/submissions?status=${status}`, { headers: { Authorization: `Bearer ${token}` } });
      setSubs((await res.json()).submissions);
    } catch (e) { console.error('locator subs fetch failed', e); }
  }, [token, status]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const review = async (id, action) => {
    setBusy(id);
    try {
      const res = await fetch(`${API_URL}/api/admin/locator/submissions/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action }),
      });
      if (!res.ok) window.alert(`${action} failed — ${(await res.json()).detail || 'try again'}`);
      fetchSubs();
    } catch {
      window.alert(`${action} failed — network error, try again`);
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="locator-admin-tab">
      <AnalyticsCard token={token} />
      <ReferralDigestCard token={token} />
      <ProfileEditorCard token={token} />
      <OutreachCard token={token} />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Recovery Locator — Claims &amp; Submissions</h2>
          <p className="text-sm text-gray-500">Facility owners claiming listings or submitting new facilities from /locator</p>
        </div>
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected'].map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 text-xs font-semibold rounded-lg capitalize ${status === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`locator-status-${s}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {!subs ? (
        <div className="text-center py-8"><RefreshCw className="w-5 h-5 text-gray-400 animate-spin mx-auto" /></div>
      ) : subs.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-xl"><p className="text-gray-400 text-sm">No {status} submissions.</p></div>
      ) : (
        <div className="space-y-3">
          {subs.map((s) => (
            <div key={s.submission_id} className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`locator-sub-${s.submission_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2">
                    {s.type === 'claim' ? <BadgeCheck className="w-4 h-4 text-blue-600" /> : <Building2 className="w-4 h-4 text-emerald-600" />}
                    <h4 className="font-bold text-gray-900 text-sm">{s.facility_name}</h4>
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase rounded ${s.type === 'claim' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'}`}>
                      {s.type === 'claim' ? 'Claim' : 'New Facility'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {s.contact?.name} · {s.contact?.email}{s.contact?.role ? ` · ${s.contact.role}` : ''}{s.city ? ` · ${s.city}, ${s.state}` : ''}
                  </p>
                  {s.website && <p className="text-xs text-gray-400 mt-0.5">{s.website}</p>}
                  {s.therapies && <p className="text-xs text-gray-400 mt-0.5">Therapies: {s.therapies}</p>}
                  {s.message && <p className="text-xs text-gray-600 mt-1.5 bg-gray-50 rounded p-2">{s.message}</p>}
                  <p className="text-[11px] text-gray-400 mt-1">{new Date(s.created_at).toLocaleString()}</p>
                </div>
                {status === 'pending' && (
                  <div className="flex gap-2">
                    <button onClick={() => review(s.submission_id, 'approve')} disabled={busy === s.submission_id} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid={`locator-approve-${s.submission_id}`}>
                      <Check className="w-3 h-3" /> Approve
                    </button>
                    <button onClick={() => review(s.submission_id, 'reject')} disabled={busy === s.submission_id} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 inline-flex items-center gap-1" data-testid={`locator-reject-${s.submission_id}`}>
                      <X className="w-3 h-3" /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
